import { useState } from 'react';
import { Button, Card, Flex, Stack, Table, useTheme, spacing } from '@fti/design-system';
import { StatusBadge } from '../shared/StatusBadge';
import { EditableBillingCode } from './EditableBillingCode';
import { EditableKeyFacts } from './EditableKeyFacts';
import { StaticReflectionView } from '../reflections/StaticReflectionView';
import { useStaticReflectionStatus } from '../../../api/media-sim/reflections';
import { useSeedPublications } from '../../../api/media-sim/publications';
import { useUsers } from '../../../api/media-sim/users';
import type { Project, Publication, ProjectMember } from '../../../types/media-sim';

interface ProjectOverviewProps {
  project: Project;
  publications: Publication[];
  members: ProjectMember[];
  isAdmin: boolean;
  isCreator: boolean;
  onAddMember?: (userId: string) => void;
  onRemoveMember?: (memberId: string) => void;
  onPublicationsRefreshed?: () => void;
  /** Called after an inline field edit so the page can refetch. */
  onProjectUpdated?: () => void;
}

export function ProjectOverview({
  project,
  publications,
  members,
  isAdmin,
  isCreator,
  onAddMember,
  onRemoveMember,
  onPublicationsRefreshed,
  onProjectUpdated,
}: ProjectOverviewProps) {
  const { theme } = useTheme();
  const [userSearch, setUserSearch] = useState('');
  const [expandedPubId, setExpandedPubId] = useState<string | null>(null);
  const { data: users } = useUsers(userSearch || undefined);
  const { seed, loading: seeding } = useSeedPublications();
  const pubIds = publications.map((p) => p.id);
  const { statuses: reflectionStatuses } = useStaticReflectionStatus(pubIds);
  const canManageMembers = (isAdmin || isCreator) && project.status === 'active';

  const getReflectionStatus = (pubId: string) => {
    const s = reflectionStatuses.find((rs) => rs.publication_id === pubId);
    if (!s || !s.has_reflection) return null;
    return s.status;
  };

  const pubColumns = [
    {
      key: 'publisher',
      header: 'Publisher',
      render: (_val: unknown, row: Record<string, unknown>) => {
        const pub = row as unknown as Publication;
        return (
          <button
            onClick={() => setExpandedPubId(expandedPubId === pub.id ? null : pub.id)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: theme.colors.primary[500],
              fontWeight: 500,
              padding: 0,
              textDecoration: 'underline',
            }}
          >
            {pub.publisher}
          </button>
        );
      },
    },
    { key: 'milvus_id', header: 'Milvus ID' },
    {
      key: 'reflection_status',
      header: 'Reflection',
      render: (_val: unknown, row: Record<string, unknown>) => {
        const pub = row as unknown as Publication;
        const status = getReflectionStatus(pub.id);
        return status ? (
          <StatusBadge status={status} size="sm" />
        ) : (
          <span style={{ fontSize: '0.75rem', color: theme.colors.text.secondary }}>None</span>
        );
      },
    },
  ];

  const memberColumns = [
    { key: 'user_name', header: 'Name' },
    { key: 'user_email', header: 'Email' },
    ...(canManageMembers && onRemoveMember
      ? [{
          key: 'actions',
          header: 'Actions',
          render: (_val: unknown, row: Record<string, unknown>) => {
            const m = row as unknown as ProjectMember;
            return (
              <Button variant="ghost" size="sm" color="error" onClick={() => onRemoveMember(m.id)}>
                Remove
              </Button>
            );
          },
        }]
      : []),
  ];

  const fieldStyle = {
    fontSize: '0.9rem',
    color: theme.colors.text.primary,
  };

  const labelStyle = {
    fontSize: '0.85rem',
    fontWeight: 500 as const,
    color: theme.colors.text.secondary,
    minWidth: 120,
  };

  return (
    <Stack gap="lg">
      <Card variant="outlined">
        <Card.Header title="Project Details">
          <StatusBadge status={project.status} size="md" />
        </Card.Header>
        <Card.Body>
          <Stack gap="sm">
            <Flex gap="sm"><span style={labelStyle}>Name:</span><span style={fieldStyle}>{project.project_name}</span></Flex>
            <Flex gap="sm"><span style={labelStyle}>Client:</span><span style={fieldStyle}>{project.client_name}</span></Flex>
            <EditableBillingCode
              projectId={project.id}
              billingCode={project.billing_code}
              onSaved={onProjectUpdated}
            />
            <Flex gap="sm"><span style={labelStyle}>Model:</span><span style={fieldStyle}>{project.model_name}</span></Flex>
            <Flex gap="sm">
              <span style={labelStyle}>Start Date:</span>
              <span style={fieldStyle}>
                {project.start_date ? new Date(project.start_date).toLocaleDateString() : '—'}
              </span>
            </Flex>
            <Flex gap="sm">
              <span style={labelStyle}>Created By:</span>
              <span style={fieldStyle}>
                {project.creator_name || project.creator_email || '—'}
              </span>
            </Flex>
            <Flex gap="sm">
              <span style={labelStyle}>Created:</span>
              <span style={fieldStyle}>
                {new Date(project.created_at).toLocaleDateString()}
              </span>
            </Flex>
            <Flex gap="sm" align="start">
              <span style={labelStyle}>Topic Terms:</span>
              <Flex gap="xs" wrap="wrap">
                {project.topic_terms.map((t, i) => (
                  <span key={i} style={{ padding: `2px ${spacing.xs}`, backgroundColor: theme.colors.primary[100], borderRadius: '8px', fontSize: '0.8rem' }}>{t}</span>
                ))}
              </Flex>
            </Flex>
            <Flex gap="sm" align="start">
              <span style={labelStyle}>Subject Terms:</span>
              <Flex gap="xs" wrap="wrap">
                {project.subject_terms.map((t, i) => (
                  <span key={i} style={{ padding: `2px ${spacing.xs}`, backgroundColor: theme.colors.primary[100], borderRadius: '8px', fontSize: '0.8rem' }}>{t}</span>
                ))}
              </Flex>
            </Flex>
          </Stack>
        </Card.Body>
      </Card>

      <Card variant="outlined">
        <Card.Header title={`Publications (${publications.length})`}>
          <Button
            variant="outline"
            size="sm"
            disabled={seeding}
            onClick={async () => {
              await seed();
              onPublicationsRefreshed?.();
            }}
          >
            {seeding ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Card.Header>
        <Card.Body>
          {publications.length > 0 ? (
            <Stack gap="md">
              <Table data={publications as unknown as Record<string, unknown>[]} columns={pubColumns} />
              {expandedPubId && (
                <StaticReflectionView
                  publicationId={expandedPubId}
                  isAdmin={isAdmin}
                />
              )}
            </Stack>
          ) : (
            <p style={{ color: theme.colors.text.secondary, margin: 0 }}>No publications assigned.</p>
          )}
        </Card.Body>
      </Card>

      <Card variant="outlined">
        <Card.Header title="Key Facts" />
        <Card.Body>
          <EditableKeyFacts
            projectId={project.id}
            keyFacts={project.key_facts}
            onSaved={onProjectUpdated}
          />
        </Card.Body>
      </Card>

      <Card variant="outlined">
        <Card.Header title={`Members (${members.length})`} />
        <Card.Body>
          {canManageMembers && onAddMember && (
            <Flex gap="xs" align="center" style={{ marginBottom: spacing.sm }}>
              <input
                type="text"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search users to add..."
                style={{
                  flex: 1,
                  padding: `${spacing.xs} ${spacing.sm}`,
                  border: `1px solid ${theme.colors.border.default}`,
                  borderRadius: '4px',
                  backgroundColor: theme.colors.background.paper,
                  color: theme.colors.text.primary,
                  fontSize: '0.9rem',
                }}
              />
            </Flex>
          )}
          {userSearch.length >= 2 && canManageMembers && onAddMember && (
            <Card variant="outlined" style={{ marginBottom: spacing.sm }}>
              <Card.Body>
                {!users ? (
                  <p style={{ fontSize: '0.85rem', color: theme.colors.text.secondary, margin: 0 }}>Searching...</p>
                ) : users.filter((u) => !members.some((m) => m.user_id === u.user_id)).length === 0 ? (
                  <p style={{ fontSize: '0.85rem', color: theme.colors.text.secondary, margin: 0 }}>No matching users found</p>
                ) : (
                  users
                    .filter((u) => !members.some((m) => m.user_id === u.user_id))
                    .map((u) => (
                      <Flex
                        key={u.user_id}
                        justify="between"
                        align="center"
                        style={{
                          padding: `${spacing.xs} ${spacing.sm}`,
                          cursor: 'pointer',
                          borderRadius: '4px',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = theme.colors.background.paper)}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                        onClick={() => { onAddMember(u.user_id); setUserSearch(''); }}
                      >
                        <Stack gap="xs">
                          <span style={{ fontSize: '0.85rem', fontWeight: 500, color: theme.colors.text.primary }}>{u.name}</span>
                          <span style={{ fontSize: '0.75rem', color: theme.colors.text.secondary }}>{u.email}</span>
                        </Stack>
                      </Flex>
                    ))
                )}
              </Card.Body>
            </Card>
          )}
          {members.length > 0 ? (
            <Table data={members as unknown as Record<string, unknown>[]} columns={memberColumns} />
          ) : (
            <p style={{ color: theme.colors.text.secondary, margin: 0 }}>No members added.</p>
          )}
        </Card.Body>
      </Card>
    </Stack>
  );
}
